import { useMemo } from 'react';

interface GeoData {
  countryCode: string;
  countryName: string;
  isEU: boolean;
  isLoading: boolean;
}

const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

const LOCALE_TO_COUNTRY: Record<string, [string, string]> = {
  'af-ZA': ['ZA', 'South Africa'],
  'ar-AE': ['AE', 'United Arab Emirates'],
  'ar-BH': ['BH', 'Bahrain'],
  'ar-DZ': ['DZ', 'Algeria'],
  'ar-EG': ['EG', 'Egypt'],
  'ar-IQ': ['IQ', 'Iraq'],
  'ar-JO': ['JO', 'Jordan'],
  'ar-KW': ['KW', 'Kuwait'],
  'ar-LB': ['LB', 'Lebanon'],
  'ar-LY': ['LY', 'Libya'],
  'ar-MA': ['MA', 'Morocco'],
  'ar-OM': ['OM', 'Oman'],
  'ar-QA': ['QA', 'Qatar'],
  'ar-SA': ['SA', 'Saudi Arabia'],
  'ar-TN': ['TN', 'Tunisia'],
  'ar-YE': ['YE', 'Yemen'],
  'bg-BG': ['BG', 'Bulgaria'],
  'bn-BD': ['BD', 'Bangladesh'],
  'bn-IN': ['IN', 'India'],
  'ca-ES': ['ES', 'Spain'],
  'cs-CZ': ['CZ', 'Czech Republic'],
  'cy-GB': ['GB', 'United Kingdom'],
  'da-DK': ['DK', 'Denmark'],
  'de-AT': ['AT', 'Austria'],
  'de-CH': ['CH', 'Switzerland'],
  'de-DE': ['DE', 'Germany'],
  'de-LI': ['LI', 'Liechtenstein'],
  'de-LU': ['LU', 'Luxembourg'],
  'el-CY': ['CY', 'Cyprus'],
  'el-GR': ['GR', 'Greece'],
  'en-AU': ['AU', 'Australia'],
  'en-BZ': ['BZ', 'Belize'],
  'en-CA': ['CA', 'Canada'],
  'en-GB': ['GB', 'United Kingdom'],
  'en-IE': ['IE', 'Ireland'],
  'en-IN': ['IN', 'India'],
  'en-JM': ['JM', 'Jamaica'],
  'en-MY': ['MY', 'Malaysia'],
  'en-NZ': ['NZ', 'New Zealand'],
  'en-PH': ['PH', 'Philippines'],
  'en-SG': ['SG', 'Singapore'],
  'en-TT': ['TT', 'Trinidad and Tobago'],
  'en-US': ['US', 'United States'],
  'en-ZA': ['ZA', 'South Africa'],
  'en-ZW': ['ZW', 'Zimbabwe'],
  'es-AR': ['AR', 'Argentina'],
  'es-BO': ['BO', 'Bolivia'],
  'es-CL': ['CL', 'Chile'],
  'es-CO': ['CO', 'Colombia'],
  'es-CR': ['CR', 'Costa Rica'],
  'es-CU': ['CU', 'Cuba'],
  'es-DO': ['DO', 'Dominican Republic'],
  'es-EC': ['EC', 'Ecuador'],
  'es-ES': ['ES', 'Spain'],
  'es-GT': ['GT', 'Guatemala'],
  'es-HN': ['HN', 'Honduras'],
  'es-MX': ['MX', 'Mexico'],
  'es-NI': ['NI', 'Nicaragua'],
  'es-PA': ['PA', 'Panama'],
  'es-PE': ['PE', 'Peru'],
  'es-PR': ['PR', 'Puerto Rico'],
  'es-PY': ['PY', 'Paraguay'],
  'es-SV': ['SV', 'El Salvador'],
  'es-US': ['US', 'United States'],
  'es-UY': ['UY', 'Uruguay'],
  'es-VE': ['VE', 'Venezuela'],
  'et-EE': ['EE', 'Estonia'],
  'eu-ES': ['ES', 'Spain'],
  'fa-IR': ['IR', 'Iran'],
  'fi-FI': ['FI', 'Finland'],
  'fil-PH': ['PH', 'Philippines'],
  'fr-BE': ['BE', 'Belgium'],
  'fr-CA': ['CA', 'Canada'],
  'fr-CH': ['CH', 'Switzerland'],
  'fr-FR': ['FR', 'France'],
  'fr-LU': ['LU', 'Luxembourg'],
  'fr-MC': ['MC', 'Monaco'],
  'ga-IE': ['IE', 'Ireland'],
  'gl-ES': ['ES', 'Spain'],
  'gu-IN': ['IN', 'India'],
  'he-IL': ['IL', 'Israel'],
  'hi-IN': ['IN', 'India'],
  'hr-BA': ['BA', 'Bosnia and Herzegovina'],
  'hr-HR': ['HR', 'Croatia'],
  'hu-HU': ['HU', 'Hungary'],
  'hy-AM': ['AM', 'Armenia'],
  'id-ID': ['ID', 'Indonesia'],
  'is-IS': ['IS', 'Iceland'],
  'it-CH': ['CH', 'Switzerland'],
  'it-IT': ['IT', 'Italy'],
  'ja-JP': ['JP', 'Japan'],
  'ka-GE': ['GE', 'Georgia'],
  'kk-KZ': ['KZ', 'Kazakhstan'],
  'kn-IN': ['IN', 'India'],
  'ko-KR': ['KR', 'South Korea'],
  'lt-LT': ['LT', 'Lithuania'],
  'lv-LV': ['LV', 'Latvia'],
  'mk-MK': ['MK', 'North Macedonia'],
  'ml-IN': ['IN', 'India'],
  'mn-MN': ['MN', 'Mongolia'],
  'mr-IN': ['IN', 'India'],
  'ms-BN': ['BN', 'Brunei'],
  'ms-MY': ['MY', 'Malaysia'],
  'mt-MT': ['MT', 'Malta'],
  'my-MM': ['MM', 'Myanmar'],
  'nb-NO': ['NO', 'Norway'],
  'ne-NP': ['NP', 'Nepal'],
  'nl-BE': ['BE', 'Belgium'],
  'nl-NL': ['NL', 'Netherlands'],
  'nn-NO': ['NO', 'Norway'],
  'pa-IN': ['IN', 'India'],
  'pl-PL': ['PL', 'Poland'],
  'pt-BR': ['BR', 'Brazil'],
  'pt-PT': ['PT', 'Portugal'],
  'ro-MD': ['MD', 'Moldova'],
  'ro-RO': ['RO', 'Romania'],
  'ru-BY': ['BY', 'Belarus'],
  'ru-KG': ['KG', 'Kyrgyzstan'],
  'ru-KZ': ['KZ', 'Kazakhstan'],
  'ru-RU': ['RU', 'Russia'],
  'ru-UA': ['UA', 'Ukraine'],
  'si-LK': ['LK', 'Sri Lanka'],
  'sk-SK': ['SK', 'Slovakia'],
  'sl-SI': ['SI', 'Slovenia'],
  'sq-AL': ['AL', 'Albania'],
  'sr-RS': ['RS', 'Serbia'],
  'sv-FI': ['FI', 'Finland'],
  'sv-SE': ['SE', 'Sweden'],
  'sw-KE': ['KE', 'Kenya'],
  'sw-TZ': ['TZ', 'Tanzania'],
  'ta-IN': ['IN', 'India'],
  'ta-LK': ['LK', 'Sri Lanka'],
  'ta-MY': ['MY', 'Malaysia'],
  'ta-SG': ['SG', 'Singapore'],
  'te-IN': ['IN', 'India'],
  'th-TH': ['TH', 'Thailand'],
  'tr-CY': ['CY', 'Cyprus'],
  'tr-TR': ['TR', 'Turkey'],
  'uk-UA': ['UA', 'Ukraine'],
  'ur-IN': ['IN', 'India'],
  'ur-PK': ['PK', 'Pakistan'],
  'vi-VN': ['VN', 'Vietnam'],
  'zh-CN': ['CN', 'China'],
  'zh-HK': ['HK', 'Hong Kong'],
  'zh-MO': ['MO', 'Macau'],
  'zh-SG': ['SG', 'Singapore'],
  'zh-TW': ['TW', 'Taiwan'],
  'zu-ZA': ['ZA', 'South Africa'],
};

const LANG_FALLBACK: Record<string, [string, string]> = {
  'ar': ['SA', 'Saudi Arabia'],
  'bg': ['BG', 'Bulgaria'],
  'bn': ['BD', 'Bangladesh'],
  'ca': ['ES', 'Spain'],
  'cs': ['CZ', 'Czech Republic'],
  'da': ['DK', 'Denmark'],
  'de': ['DE', 'Germany'],
  'el': ['GR', 'Greece'],
  'en': ['US', 'United States'],
  'es': ['ES', 'Spain'],
  'et': ['EE', 'Estonia'],
  'fa': ['IR', 'Iran'],
  'fi': ['FI', 'Finland'],
  'fil': ['PH', 'Philippines'],
  'fr': ['FR', 'France'],
  'gl': ['ES', 'Spain'],
  'gu': ['IN', 'India'],
  'he': ['IL', 'Israel'],
  'hi': ['IN', 'India'],
  'hr': ['HR', 'Croatia'],
  'hu': ['HU', 'Hungary'],
  'hy': ['AM', 'Armenia'],
  'id': ['ID', 'Indonesia'],
  'is': ['IS', 'Iceland'],
  'it': ['IT', 'Italy'],
  'ja': ['JP', 'Japan'],
  'ka': ['GE', 'Georgia'],
  'kk': ['KZ', 'Kazakhstan'],
  'kn': ['IN', 'India'],
  'ko': ['KR', 'South Korea'],
  'lt': ['LT', 'Lithuania'],
  'lv': ['LV', 'Latvia'],
  'mk': ['MK', 'North Macedonia'],
  'ml': ['IN', 'India'],
  'mn': ['MN', 'Mongolia'],
  'mr': ['IN', 'India'],
  'ms': ['MY', 'Malaysia'],
  'mt': ['MT', 'Malta'],
  'my': ['MM', 'Myanmar'],
  'nb': ['NO', 'Norway'],
  'ne': ['NP', 'Nepal'],
  'nl': ['NL', 'Netherlands'],
  'nn': ['NO', 'Norway'],
  'pa': ['IN', 'India'],
  'pl': ['PL', 'Poland'],
  'pt': ['BR', 'Brazil'],
  'ro': ['RO', 'Romania'],
  'ru': ['RU', 'Russia'],
  'si': ['LK', 'Sri Lanka'],
  'sk': ['SK', 'Slovakia'],
  'sl': ['SI', 'Slovenia'],
  'sq': ['AL', 'Albania'],
  'sr': ['RS', 'Serbia'],
  'sv': ['SE', 'Sweden'],
  'sw': ['KE', 'Kenya'],
  'ta': ['IN', 'India'],
  'te': ['IN', 'India'],
  'th': ['TH', 'Thailand'],
  'tr': ['TR', 'Turkey'],
  'uk': ['UA', 'Ukraine'],
  'ur': ['PK', 'Pakistan'],
  'vi': ['VN', 'Vietnam'],
  'zh': ['CN', 'China'],
  'zu': ['ZA', 'South Africa'],
};

function detectCountryFromLocale(): { countryCode: string; countryName: string } {
  const locale = navigator.language || '';
  const normalized = locale.replace('_', '-');

  const exact = LOCALE_TO_COUNTRY[normalized];
  if (exact) return { countryCode: exact[0], countryName: exact[1] };

  const caseInsensitive = Object.entries(LOCALE_TO_COUNTRY).find(
    ([key]) => key.toLowerCase() === normalized.toLowerCase()
  );
  if (caseInsensitive) return { countryCode: caseInsensitive[1][0], countryName: caseInsensitive[1][1] };

  const langOnly = normalized.split('-')[0].toLowerCase();
  const fallback = LANG_FALLBACK[langOnly];
  if (fallback) return { countryCode: fallback[0], countryName: fallback[1] };

  return { countryCode: '', countryName: '' };
}

export function useGeoLocation(): GeoData {
  return useMemo(() => {
    const { countryCode, countryName } = detectCountryFromLocale();
    return {
      countryCode,
      countryName,
      isEU: EU_COUNTRY_CODES.has(countryCode),
      isLoading: false,
    };
  }, []);
}
